import React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

function DetailTile({ label, value, valueColor = "text.primary" }) {
  return (
    <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: "background.subtle" }}>
      <Typography sx={{ fontSize: 14, color: "text.muted" }}>{label}</Typography>
      <Typography sx={{ fontSize: 16, color: valueColor, mt: 0.5 }}>{value}</Typography>
    </Box>
  );
}

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography sx={{ fontSize: 14, color: "text.muted" }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, color: "text.primary" }}>{value}</Typography>
    </Stack>
  );
}

export default function PositionDetailsModal({ open, onClose, position }) {
  if (!position) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Typography fontWeight={700} fontSize={18} sx={{ color: "text.primary" }}>
          Staking Position Details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          View comprehensive information about your staking position.
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ position: "absolute", right: 12, top: 12 }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: "border.subtle" }}>
        <Stack spacing={2.5}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: 1.5,
              border: (theme) => `1px solid ${theme.palette.accent.teal.border}`,
              bgcolor: (theme) => theme.palette.accent.cyan.bgLight,
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "text.primary" }}>
              {position.name}
            </Typography>
            <Chip
              label={position.status}
              size="small"
              sx={{
                mt: 1,
                textTransform: "capitalize",
                bgcolor: (theme) => theme.palette.chips.success.text,
                color: "common.white",
                fontWeight: 600,
                fontSize: 11,
                height: 20,
                borderRadius: 999,
              }}
            />
          </Box>

          <Grid container spacing={2}>
            <Grid size={6}>
              <DetailTile label="Staked Amount" value={position.staked} />
            </Grid>
            <Grid size={6}>
              <DetailTile
                label="Current APY"
                value={position.apy}
                valueColor={(theme) => theme.palette.chips.success.text}
              />
            </Grid>
            <Grid size={6}>
              <DetailTile label="Total Earned" value={position.earned} />
            </Grid>
            <Grid size={6}>
              <DetailTile label="Days Remaining" value={position.daysRemaining ?? "—"} />
            </Grid>
          </Grid>

          <Divider />

          <Stack spacing={1.25}>
            <DetailRow label="Start Date" value={position.startDate} />
            <DetailRow label="End Date" value={position.endDate} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: 14, color: "text.muted" }}>Status</Typography>
              <Chip
                label={position.status}
                size="small"
                sx={{
                  textTransform: "capitalize",
                  bgcolor: (theme) => theme.palette.chips.success.bg,
                  color: (theme) => theme.palette.chips.success.text,
                  fontSize: 11,
                  fontWeight: 600,
                  height: 22,
                  borderRadius: 999,
                }}
              />
            </Stack>
          </Stack>

          {/* <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            sx={{
              p: 2,
              borderRadius: 1.5,
              bgcolor: (theme) => theme.palette.chips.warning.bg,
              border: (theme) => `1px solid ${theme.palette.chips.warning.text}33`,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 20, color: (theme) => theme.palette.chips.warning.text, mt: 0.25 }} />
            <Typography sx={{ fontSize: 14, color: (theme) => theme.palette.chips.warning.text }}>
              Early withdrawal may incur penalties. Your tokens will automatically unlock on {position.endDate}.
            </Typography>
          </Stack> */}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: "none", fontWeight: 600 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
